export type DateInputResolution = {
  rawValue: string;
  parsedDate: Date | null;
  value: string | null;
};

export default class DateInputHelper {
  private static readonly ukDatePattern = /^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/;

  private static readonly isoLikeDatePattern = /^(\d{4})-(\d{2})-(\d{2})$/;

  static parseUkInput(dateStr: string) {
    const match = dateStr.match(this.ukDatePattern);
    if (!match) return null;

    const day = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    let year = parseInt(match[3], 10);

    if (year < 100) {
      year += 2000;
    }

    const date = new Date(Date.UTC(year, month, day));

    const isValid =
      !isNaN(date.getTime()) &&
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month &&
      date.getUTCDate() === day;

    return isValid ? date : null;
  }

  static parseIsoLikeInput(dateStr: string) {
    const match = dateStr.match(this.isoLikeDatePattern);
    if (!match) return null;

    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10) - 1;
    const day = parseInt(match[3], 10);

    const date = new Date(Date.UTC(year, month, day));

    const isValid =
      !isNaN(date.getTime()) &&
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month &&
      date.getUTCDate() === day;

    return isValid ? date : null;
  }

  static parseAnyDate(dateStr: string) {
    const value = String(dateStr ?? '').trim();
    if (!value) return null;

    const uk = this.parseUkInput(value);
    if (uk) return uk;

    if (value.includes('/')) return null;

    const iso = this.parseIsoLikeInput(value);
    if (iso) return iso;

    const fallback = new Date(value);
    return isNaN(fallback.getTime())
      ? null
      : new Date(Date.UTC(fallback.getFullYear(), fallback.getMonth(), fallback.getDate()));
  }

  static resolveDateValue(input: unknown): DateInputResolution {
    const rawValue = String(input ?? '').trim();

    if (!rawValue) {
      return {
        rawValue: '',
        parsedDate: null,
        value: null,
      };
    }

    const date = this.parseAnyDate(rawValue);

    if (!date) {
      return {
        rawValue,
        parsedDate: null,
        value: null,
      };
    }

    const formatted = this.formatDate(date);

    return {
      rawValue,
      parsedDate: date,
      value: formatted,
    };
  }

  static formatDate(date: Date) {
    const day = String(date.getUTCDate()).padStart(2, '0');
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const year = date.getUTCFullYear();

    return `${day}/${month}/${year}`;
  }

  static getTodayUtcTimestamp(): number {
    const today = new Date();

    return Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  }

  static isFutureDate(date: Date): boolean {
    return date.getTime() > this.getTodayUtcTimestamp();
  }

  static isPastOrToday(date: Date): boolean {
    return date.getTime() <= this.getTodayUtcTimestamp();
  }
}
